import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { globalSearch } from '../../api/searchApi';

/* GLOBAL SEARCH — the top-bar typeahead, modelled on FieldRoutes.

   WHY THIS FILE EXISTS RATHER THAN A SMALL PATCH: the old inline version
   worked. It debounced, it called a working endpoint, it rendered its rows —
   and `.top-bar-search { overflow: hidden }` clipped the absolutely-positioned
   dropdown out of existence, so typing "2812" looked like it did nothing for
   seven months. That CSS is fixed; this adds what FieldRoutes actually gives
   you and, critically, stops a failure from ever looking like an empty box
   again.

   TWO RESPONSE SHAPES ON PURPOSE. The frontend ships before the richer
   /api/search lands (different deploys, different locks), so every enriched
   field — local/FR ids, rep, office — is read defensively and simply doesn't
   render until the backend supplies it. Nothing here breaks against today's
   {label, subtitle, phone} response, and nothing waits on the backend either.

   NO SILENT NOTHING. A failed request says so and offers a retry; zero results
   says "No match for …". The previous `catch {}` set results to [] and closed
   the panel, which is indistinguishable from "nothing matched" — and that is
   what made a clipped dropdown so hard to diagnose. */

const DEBOUNCE_MS = 150;
const MIN_CHARS = 2;
const MAX_ROWS = 12;

const clean = (v) => String(v ?? '').trim();
const digits = (v) => String(v ?? '').replace(/\D/g, '');

const prettyPhone = (value) => {
  const d = digits(value);
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  return clean(value);
};

/* Highlight every run of the query inside a field, including inside a
   formatted phone number: the user typed "2812" but the row shows
   "(239) 281-2399", so a naive substring match on the rendered string finds
   nothing. For phones we map digit positions back to display positions. */
function highlight(text, query) {
  const value = clean(text);
  const q = clean(query);
  if (!value || !q) return value;

  const lowerValue = value.toLowerCase();
  const lowerQ = q.toLowerCase();
  const parts = [];
  let cursor = 0;
  let at = lowerValue.indexOf(lowerQ);
  while (at !== -1) {
    if (at > cursor) parts.push(value.slice(cursor, at));
    parts.push(<mark key={`${at}-${parts.length}`}>{value.slice(at, at + q.length)}</mark>);
    cursor = at + q.length;
    at = lowerValue.indexOf(lowerQ, cursor);
  }
  if (!parts.length) return value;
  if (cursor < value.length) parts.push(value.slice(cursor));
  return parts;
}

/** Highlight a phone by matching on DIGITS and mapping back to the display. */
function highlightPhone(display, query) {
  const shown = clean(display);
  const qd = digits(query);
  if (!shown || qd.length < MIN_CHARS) return shown;

  // digit index -> index in the display string
  const map = [];
  for (let i = 0; i < shown.length; i += 1) {
    if (/\d/.test(shown[i])) map.push(i);
  }
  const shownDigits = map.map((i) => shown[i]).join('');
  const hit = shownDigits.indexOf(qd);
  if (hit === -1) return shown;

  const start = map[hit];
  const end = map[hit + qd.length - 1] + 1;
  return [
    shown.slice(0, start),
    <mark key="ph">{shown.slice(start, end)}</mark>,
    shown.slice(end),
  ];
}

/* Normalise a row from either response shape. Enriched fields stay undefined
   rather than being invented — a row must never show a confident-looking id or
   rep that the server did not send. */
function normaliseRow(raw) {
  const route = raw?.route || {};
  return {
    key: `${raw?.result_type || 'row'}-${raw?.id ?? Math.random()}`,
    type: clean(raw?.result_type) || 'record',
    id: raw?.id,
    label: clean(raw?.label) || 'Untitled',
    subtitle: clean(raw?.subtitle),
    phone: clean(raw?.phone),
    // v3 fields. `idLine` is composed SERVER-side ("C-1200 · FR 4199") and
    // rendered verbatim — composing it here would mean the dropdown and the
    // customer record could disagree about a customer's own number.
    idLine: clean(raw?.id_line),
    frId: clean(raw?.fieldroutes_id),
    frAccount: clean(raw?.fieldroutes_account),
    accountNo: clean(raw?.account_number),
    rep: clean(raw?.assigned_to),
    address: clean(raw?.address),
    office: clean(raw?.office),
    // Which field the server matched on, so the highlight lands on the field
    // that actually caused the hit instead of every field that happens to
    // contain the string.
    matchedKey: clean(raw?.matched_key),
    // Cancelled accounts are RETURNED, not hidden — FieldRoutes shows them in
    // pink. `statusLabel` is the server's word for it.
    isActive: raw?.is_active !== false,
    statusLabel: clean(raw?.status_label),
    leadSource: clean(raw?.lead_source),
    stage: clean(raw?.stage),
    route,
    raw,
  };
}

export default function TopBarSearch({ placeholder, onSelect, autoFocus = false }) {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [active, setActive] = useState(0);
  // The query the CURRENT rows belong to, so "No match for X" can never show
  // X while displaying results for Y.
  const [answered, setAnswered] = useState('');

  const inputRef = useRef(null);
  const blurTimer = useRef(null);

  const trimmed = query.trim();
  const tooShort = trimmed.length < MIN_CHARS;

  const focusSearch = useCallback(() => {
    setExpanded(true);
    inputRef.current?.focus();
  }, []);

  // The phone bar mounts the search already open — focus it at once.
  useEffect(() => { if (autoFocus) focusSearch(); }, [autoFocus, focusSearch]);

  // Cmd/Ctrl+K and "/" focus the box. "/" is ignored while the user is typing
  // in any other field, or it would hijack every form on the page.
  useEffect(() => {
    const onKey = (e) => {
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      const slash = e.key === '/';
      if (!cmdK && !slash) return;
      const el = document.activeElement;
      const typing =
        el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'SELECT' ||
          el.isContentEditable);
      if (slash && typing) return;
      e.preventDefault();
      focusSearch();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusSearch]);

  useEffect(() => {
    let cancelled = false;
    if (tooShort) {
      setRows([]);
      setLeads([]);
      setOpen(false);
      setError('');
      setLoading(false);
      setAnswered('');
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await globalSearch(trimmed);
        if (cancelled) return;
        const list = Array.isArray(data?.results) ? data.results : [];
        const leadList = Array.isArray(data?.leads) ? data.leads : [];
        setRows(list.slice(0, MAX_ROWS).map(normaliseRow));
        setLeads(leadList.slice(0, MAX_ROWS).map(normaliseRow));
        setError('');
        setActive(0);
        setAnswered(trimmed);
        setOpen(true);
      } catch (err) {
        if (cancelled) return;
        // A failure is stated, never rendered as an empty result set.
        const status = err?.response?.status;
        setRows([]);
        setLeads([]);
        setAnswered(trimmed);
        setError(
          status === 401 || status === 403
            ? 'Your session expired. Sign in again to search.'
            : "Search isn't responding right now.",
        );
        setOpen(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [trimmed, tooShort]);

  const retry = () => {
    // Re-run the same query by nudging the debounce key.
    setError('');
    setQuery((q) => `${q} `.slice(0, -1) || q);
    inputRef.current?.focus();
  };

  const choose = (row) => {
    if (!row) return;
    onSelect?.(row);
    setOpen(false);
    setExpanded(false);
    setQuery('');
    setRows([]);
    inputRef.current?.blur();
  };

  const collapse = () => {
    setOpen(false);
    setExpanded(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (open) {
        collapse();
      } else {
        setQuery('');
        collapse();
        inputRef.current?.blur();
      }
      return;
    }
    // Arrow keys walk customers THEN leads as one list, so Enter always opens
    // the row that is visibly highlighted.
    const all = rows.concat(leads);
    if (!open || !all.length) {
      if (e.key === 'Enter') e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % all.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + all.length) % all.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(all[active]);
    }
  };

  // "C-1200 · FR 4199", server-composed. Either half may be absent and the
  // separator only appears between two real values, so this renders as-is.
  const idLine = (row) =>
    row.idLine ? <span className="tbs-ids">{highlight(row.idLine, trimmed)}</span> : null;

  const showPanel = open && (loading || rows.length > 0 || leads.length > 0 || !!error || !!answered);

  const body = useMemo(() => {
    if (error) {
      return (
        <div className="tbs-state tbs-state-error" role="alert">
          <span>{error}</span>
          <button type="button" className="tbs-retry" onClick={retry}>
            Try again
          </button>
        </div>
      );
    }
    if (loading && !rows.length && !leads.length) {
      return <div className="tbs-state">Searching…</div>;
    }
    if (!rows.length && !leads.length && answered) {
      return <div className="tbs-state">No match for “{answered}”.</div>;
    }
    const customerRow = (row, i) => (
      <button
        key={row.key}
        type="button"
        className={`tbs-row${i === active ? ' is-active' : ''}${row.isActive ? '' : ' is-cancelled'}`}
        onMouseEnter={() => setActive(i)}
        onClick={() => choose(row)}
      >
        <span className="tbs-row-main">
          <span className="tbs-name">{highlight(row.label, trimmed)}</span>
          {idLine(row)}
          {/* FieldRoutes marks a cancelled account so you cannot mistake it for
              a live one. A badge rather than colour alone, because colour is not
              a label. */}
          {row.statusLabel ? <span className="tbs-cancelled">{row.statusLabel}</span> : null}
        </span>
        <span className="tbs-row-meta">
          {row.address ? <span>{highlight(row.address, trimmed)}</span> : null}
          {row.phone ? <span>{highlightPhone(row.phone, trimmed)}</span> : null}
          {row.rep ? <span className="tbs-rep">{row.rep}</span> : null}
          {row.office ? <span className="tbs-office">{row.office}</span> : null}
          {!row.address && !row.phone && row.subtitle ? (
            <span>{highlight(row.subtitle, trimmed)}</span>
          ) : null}
        </span>
      </button>
    );

    const leadRow = (row, i) => (
      <button
        key={row.key}
        type="button"
        className={`tbs-row${active === rows.length + i ? ' is-active' : ''}`}
        onMouseEnter={() => setActive(rows.length + i)}
        onClick={() => choose(row)}
      >
        <span className="tbs-row-main">
          <span className="tbs-name">{highlight(row.label, trimmed)}</span>
        </span>
        <span className="tbs-row-meta">
          {row.phone ? <span>{highlightPhone(row.phone, trimmed)}</span> : null}
          {row.leadSource ? <span>{row.leadSource}</span> : null}
          {row.stage ? <span className="tbs-stage">{row.stage}</span> : null}
        </span>
      </button>
    );

    return (
      <>
        {rows.length ? (
          <>
            {/* Section headers only when BOTH kinds are present: a lone list
                does not need to be told what it is. */}
            {leads.length ? <div className="tbs-section">Customers</div> : null}
            {rows.map(customerRow)}
          </>
        ) : null}
        {leads.length ? (
          <>
            <div className="tbs-section">Leads</div>
            {leads.map(leadRow)}
          </>
        ) : null}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, leads, active, error, loading, answered, trimmed]);

  return (
    <div className={`top-bar-search${expanded ? ' is-expanded' : ''}`}>
      <FiSearch size={16} className="top-bar-search-icon" onClick={focusSearch} />
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder || 'Search customers, leads, phone, address'}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => {
          setExpanded(true);
          if (rows.length || leads.length || error || answered) setOpen(true);
        }}
        onBlur={() => {
          // Delay so a click on a row lands before the panel closes.
          clearTimeout(blurTimer.current);
          blurTimer.current = setTimeout(collapse, 140);
        }}
        aria-label="Search"
        autoComplete="off"
        spellCheck={false}
      />
      {!expanded && !trimmed ? <kbd className="tbs-kbd">⌘K</kbd> : null}
      {showPanel ? (
        <div className="top-bar-search-results" role="listbox">
          {body}
        </div>
      ) : null}
    </div>
  );
}
